import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { transactionAPI } from '../services/api';
import { Settings, Save, DollarSign, Calendar, Plus, Edit, Trash2 } from 'lucide-react';

const CemeterySettings = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    usage_fee_per_area: '',
    management_fee_per_area: '',
    management_fee_period: ''
  });

  const [settings, setSettings] = useState({
    cemetery_name: '',
    address: '',
    phone: '',
    admin_email: ''
  });

  const [costSettings, setCostSettings] = useState({
    usage_fee_per_area: '',
    management_fee_per_area: '',
    management_fee_period: ''
  });

  const [showStoneModal, setShowStoneModal] = useState(false);
  const [editingStone, setEditingStone] = useState(null);

  // 묘지 설정 조회
  const { data: cemeterySettings, isLoading } = useQuery({
    queryKey: ['cemeterySettings', user?.cemetery_id],
    queryFn: () => transactionAPI.getCemeterySettings(user.cemetery_id),
    enabled: !!user?.cemetery_id && user?.role === 'admin',
    onSuccess: (data) => {
      if (data) {
        setSettings({
          cemetery_name: data.cemetery_name || '',
          address: data.address || '',
          phone: data.phone || '',
          admin_email: data.admin_email || ''
        });
        setCostSettings({
          usage_fee_per_area: data.usage_fee_per_area || '',
          management_fee_per_area: data.management_fee_per_area || '',
          management_fee_period: data.management_fee_period || ''
        });
      }
    }
  });

  // 석물 비용 조회
  const { data: stoneCosts, isLoading: stoneCostsLoading } = useQuery({
    queryKey: ['stoneCosts', user?.cemetery_id],
    queryFn: () => transactionAPI.getStoneCosts(user.cemetery_id),
    enabled: !!user?.cemetery_id && user?.role === 'admin'
  });

  // 묘지 설정 수정 뮤테이션
  const settingsMutation = useMutation({
    mutationFn: (data) => transactionAPI.updateCemeterySettings(user.cemetery_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['cemeterySettings']);
      showToast('묘지 설정이 저장되었습니다.', 'success');
    },
    onError: (error) => {
      showToast(error.message || '설정 저장 중 오류가 발생했습니다.', 'error');
    }
  });

  // 비용 설정 수정 뮤테이션
  const costSettingsMutation = useMutation({
    mutationFn: (data) => transactionAPI.updateCemeterySettings(user.cemetery_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['cemeterySettings']);
      showToast('비용 설정이 저장되었습니다.', 'success');
    },
    onError: (error) => {
      showToast(error.message || '비용 설정 저장 중 오류가 발생했습니다.', 'error');
    }
  });

  const handleSettingsSubmit = (e) => {
    e.preventDefault();
    settingsMutation.mutate(settings);
  };

  const handleCostSettingsSubmit = (e) => {
    e.preventDefault();
    costSettingsMutation.mutate(costSettings);
  };

  const handleEditStone = (stone) => {
    setEditingStone(stone);
    setShowStoneModal(true);
  };

  const handleDeleteStone = (id) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      transactionAPI.deleteStoneCost(id).then(() => {
        queryClient.invalidateQueries(['stoneCosts']);
        showToast('석물이 삭제되었습니다.', 'success');
      }).catch((error) => {
        showToast(error.message || '삭제 중 오류가 발생했습니다.', 'error');
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW'
    }).format(amount || 0);
  };

  // 권한 체크 (Hook 호출 이후)
  if (user?.role !== 'admin') {
    return (
      <div className="p-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-red-800">접근 권한이 없습니다</h2>
          <p className="text-red-600 mt-2">묘지 설정은 관리자만 접근할 수 있습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">묘지 설정</h1>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Settings size={20} />
          <span>관리자 전용</span>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">설정을 불러오는 중...</div>
      ) : (
        <div className="grid grid-2" style={{ gap: '20px' }}>
          {/* 묘지 기본 설정 */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">묘지 기본 설정</h2>
            </div>
            <form onSubmit={handleSettingsSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  묘지명
                </label>
                <input
                  type="text"
                  value={settings.cemetery_name || ''}
                  onChange={(e) => setSettings({...settings, cemetery_name: e.target.value})}
                  className="input w-full"
                  placeholder="묘지명을 입력하세요"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  주소
                </label>
                <input
                  type="text"
                  value={settings.address || ''}
                  onChange={(e) => setSettings({...settings, address: e.target.value})}
                  className="input w-full"
                  placeholder="묘지 주소를 입력하세요"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  연락처
                </label>
                <input
                  type="text"
                  value={settings.phone || ''}
                  onChange={(e) => setSettings({...settings, phone: e.target.value})}
                  className="input w-full"
                  placeholder="연락처를 입력하세요"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  관리자 이메일
                </label>
                <input
                  type="email"
                  value={settings.admin_email || ''}
                  onChange={(e) => setSettings({...settings, admin_email: e.target.value})}
                  className="input w-full"
                  placeholder="관리자 이메일을 입력하세요"
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={settingsMutation.isLoading}
              >
                {settingsMutation.isLoading ? '저장 중...' : '설정 저장'}
              </button>
            </form>
          </div>

          {/* 비용 설정 */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">비용 설정</h2>
            </div>
            <form onSubmit={handleCostSettingsSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  사용료 (평당)
                </label>
                <input
                  type="number"
                  value={Math.floor(costSettings.usage_fee_per_area) || ''}
                  onChange={(e) => setCostSettings({...costSettings, usage_fee_per_area: parseInt(e.target.value) || 0})}
                  className="input w-full"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  관리비 (평당)
                </label>
                <input
                  type="number"
                  value={Math.floor(costSettings.management_fee_per_area) || ''}
                  onChange={(e) => setCostSettings({...costSettings, management_fee_per_area: parseInt(e.target.value) || 0})}
                  className="input w-full"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  관리비 납부 주기 (년)
                </label>
                <input
                  type="number"
                  value={Math.floor(costSettings.management_fee_period) || ''}
                  onChange={(e) => setCostSettings({...costSettings, management_fee_period: parseInt(e.target.value) || 0})}
                  className="input w-full"
                  placeholder="5"
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={costSettingsMutation.isLoading}
              >
                {costSettingsMutation.isLoading ? '저장 중...' : '비용 설정 저장'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 석물 비용 관리 */}
      <div className="card mt-6">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">석물 비용 관리</h2>
            <button
              onClick={() => setShowStoneModal(true)}
              className="btn btn-primary"
            >
              <Plus size={20} />
              석물 추가
            </button>
          </div>
        </div>

        {stoneCostsLoading ? (
          <div className="loading">석물 비용을 불러오는 중...</div>
        ) : stoneCosts?.data?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>석물명</th>
                  <th>비용</th>
                  <th>설명</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {stoneCosts.data.map((stone) => (
                  <tr key={stone.id}>
                    <td className="font-medium">{stone.name}</td>
                    <td>₩{stone.cost?.toLocaleString()}</td>
                    <td>{stone.description}</td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditStone(stone)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteStone(stone.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <p className="text-gray-500">등록된 석물이 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// 석물 비용 설정 컴포넌트
const StoneCostSettings = ({ cemeteryId }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  
  const [stoneCosts, setStoneCosts] = useState([
    { stone_type: '묘태', cost: '' },
    { stone_type: '상석', cost: '' },
    { stone_type: '비석', cost: '' }
  ]);

  // 석물 비용 조회
  const { data: costs, isLoading } = useQuery({
    queryKey: ['stoneCosts', cemeteryId],
    queryFn: () => transactionAPI.getStoneCosts(cemeteryId),
    enabled: !!cemeteryId,
    onSuccess: (data) => {
      if (data && data.stone_costs) {
        setStoneCosts(data.stone_costs.map(cost => ({
          stone_type: cost.stone_type,
          cost: cost.cost || ''
        })));
      }
    }
  });

  // 석물 비용 수정 뮤테이션
  const mutation = useMutation({
    mutationFn: (data) => transactionAPI.updateStoneCosts(cemeteryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['stoneCosts']);
      showToast('석물 비용이 저장되었습니다.', 'success');
    },
    onError: (error) => {
      showToast(error.message || '석물 비용 저장 중 오류가 발생했습니다.', 'error');
    }
  });

  const handleCostChange = (index, value) => {
    const newCosts = [...stoneCosts];
    newCosts[index].cost = value;
    setStoneCosts(newCosts);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      stone_costs: stoneCosts.map(cost => ({
        stone_type: cost.stone_type,
        cost: parseInt(cost.cost) || 0
      }))
    };
    mutation.mutate(data);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW'
    }).format(amount || 0);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        {stoneCosts.map((cost, index) => (
          <div key={index} className="flex items-center gap-4">
            <div className="w-24">
              <label className="block text-sm font-medium text-gray-700">
                {cost.stone_type}
              </label>
            </div>
            <div className="flex-1">
              <div className="relative">
                <input
                  type="number"
                  value={cost.cost}
                  onChange={(e) => handleCostChange(index, e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0"
                />
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
            </div>
            <div className="w-32 text-sm text-gray-500">
              {formatCurrency(cost.cost)}
            </div>
          </div>
        ))}

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={mutation.isLoading}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 disabled:opacity-50"
          >
            <Save size={16} />
            {mutation.isLoading ? '저장 중...' : '석물 비용 저장'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default CemeterySettings; 